import { useCallback, useEffect, useMemo, useState } from "react";
import { createSession, getConfig, getStats, submitReflection, wsUrl } from "./api";
import { getDeviceKey, resetDeviceKey } from "./device";
import { db } from "./db";
import { DEPTH_PROMPTS, SYMBOLS, type ServerMessage, type SymbolId } from "./types";
import { useAppStore } from "./store";
import { Turnstile } from "./components/Turnstile";
import "./styles.css";

function SymbolButton({ symbol, selected, onClick }: { symbol: (typeof SYMBOLS)[number]; selected: boolean; onClick: () => void }) {
  return (
    <button className={`symbol ${selected ? "selected" : ""}`} style={{ "--symbol": symbol.color } as React.CSSProperties} onClick={onClick} type="button">
      <span className="symbol-emoji">{symbol.emoji}</span>
      <span>{symbol.label}</span>
    </button>
  );
}

function DepthBadge({ level, ok }: { level: number; ok?: boolean }) {
  return (
    <span className={`depth-badge ${ok === true ? "ok" : ok === false ? "miss" : ""}`}>
      L{level}
    </span>
  );
}

function LadderViz({ chain, partnerChain, accuracy }: { chain: SymbolId[]; partnerChain?: SymbolId[]; accuracy?: boolean[] }) {
  const width = 640;
  const height = 160;
  const step = width / Math.max(1, chain.length - 1);
  return (
    <svg className="ladder-viz" viewBox={`0 0 ${width} ${height}`} role="img" aria-label="recursive mindreading ladder visualization">
      <defs>
        <linearGradient id="threadGradient" x1="0" x2="1">
          <stop offset="0" stopColor="#f8d77a" />
          <stop offset="0.5" stopColor="#9ee7ff" />
          <stop offset="1" stopColor="#d6a3ff" />
        </linearGradient>
      </defs>
      <path d={`M 18 92 C ${width * 0.25} 10, ${width * 0.75} 170, ${width - 18} 72`} fill="none" stroke="url(#threadGradient)" strokeWidth="5" strokeLinecap="round" opacity="0.72" />
      {chain.map((choice, index) => {
        const s = SYMBOLS.find((x) => x.id === choice)!;
        const x = index * step;
        const y = index % 2 ? 106 : 58;
        const ok = index === 0 ? undefined : accuracy?.[index];
        return (
          <g key={`${choice}-${index}`} transform={`translate(${Math.max(24, Math.min(width - 24, x))} ${y})`}>
            <circle r="22" fill={s.color} opacity="0.2" />
            <circle r="13" fill={s.color} opacity="0.85" />
            <text y="5" textAnchor="middle" fontSize="13" fill="#07101f" fontWeight="800">
              {s.emoji}
            </text>
            <text y="42" textAnchor="middle" fontSize="13" fill={ok === false ? "#ff9aa9" : "#dcecff"}>
              L{index}{ok === true ? " ✓" : ok === false ? " ×" : ""}
            </text>
          </g>
        );
      })}
      {partnerChain && (
        <text x="16" y="150" fill="#9bb4d0" fontSize="13">
          Partner chain: {partnerChain.map((id) => SYMBOLS.find((s) => s.id === id)?.emoji).join(" → ")}
        </text>
      )}
    </svg>
  );
}

function CommonsPanel() {
  const stats = useAppStore((s) => s.stats);
  const histogram = stats?.frontierHistogram ?? {};
  const max = Math.max(1, ...Object.values(histogram).map(Number));
  return (
    <aside className="panel commons">
      <div className="eyebrow">Commons map</div>
      <h2>What the crowd is learning</h2>
      {!stats ? (
        <p>Loading aggregate signals…</p>
      ) : (
        <>
          <div className="stat-grid">
            <div><strong>{stats.totalRounds}</strong><span>round rows</span></div>
            <div><strong>{stats.uniqueDevicesApprox}</strong><span>devices</span></div>
            <div><strong>L{stats.averageFrontier}</strong><span>avg frontier</span></div>
            <div><strong>{Math.round(stats.medianReactionMs / 100) / 10}s</strong><span>median</span></div>
          </div>
          <div className="histogram" aria-label="frontier depth histogram">
            {Object.entries(histogram).map(([depth, count]) => (
              <div className="bar-row" key={depth}>
                <span>L{depth}</span>
                <div className="bar"><i style={{ width: `${(Number(count) / max) * 100}%` }} /></div>
                <b>{count}</b>
              </div>
            ))}
          </div>
          <p className="tiny">Aggregates only. No names, no raw device key, no raw IP.</p>
        </>
      )}
    </aside>
  );
}

function ConsentGate() {
  const config = useAppStore((s) => s.config);
  const setSession = useAppStore((s) => s.setSession);
  const setSocket = useAppStore((s) => s.setSocket);
  const handleServerMessage = useAppStore((s) => s.handleServerMessage);
  const depth = useAppStore((s) => s.maxDepthPreference);
  const setDepth = useAppStore((s) => s.setMaxDepthPreference);
  const setError = useAppStore((s) => s.setError);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [agreed, setAgreed] = useState(false);

  const enter = async () => {
    if (!config || !agreed) return;
    setBusy(true);
    setError(undefined);
    try {
      const session = await createSession(getDeviceKey(), token, config.consentVersion);
      setSession(session);
      const socket = new WebSocket(wsUrl(session.token, depth));
      socket.addEventListener("message", (event) => handleServerMessage(JSON.parse(event.data) as ServerMessage));
      socket.addEventListener("open", () => socket.send(JSON.stringify({ type: "ready", maxDepth: depth, consentVersion: config.consentVersion })));
      socket.addEventListener("close", () => setError("Connection closed. Refresh or enter again."));
      setSocket(socket);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="hero-grid">
      <div className="hero panel">
        <div className="eyebrow">Recursive mindreading · higher-order theory of mind</div>
        <h1>How many minds can you hold before the thread snaps?</h1>
        <p>
          Mindfold pairs you with a stranger. You both choose a symbol, then predict their choice, their prediction of your choice, their prediction of your prediction, and so on. The game scores the deepest accurate link and then trains the next one.
        </p>
        <div className="depth-picker">
          <label htmlFor="depth">Maximum ladder depth</label>
          <input id="depth" type="range" min="2" max="6" value={depth} onChange={(e) => setDepth(Number(e.target.value))} />
          <strong>L{depth}</strong>
        </div>
        <label className="consent-line">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />
          <span>{config?.consentLine}</span>
        </label>
        <Turnstile siteKey={config?.turnstileSiteKey || import.meta.env.VITE_TURNSTILE_SITE_KEY} onToken={setToken} />
        <button className="primary" disabled={!agreed || !token || busy} onClick={enter} type="button">
          {busy ? "Opening the fold…" : "Enter real-time match"}
        </button>
        <button className="ghost" type="button" onClick={() => { resetDeviceKey(); location.reload(); }}>
          Reset anonymous device key
        </button>
      </div>
      <div className="panel doctrine">
        <h2>The form</h2>
        <p>
          This is not a personality test. It is a paired recursive-belief instrument: each level is checked against the stranger’s adjacent declared model.
        </p>
        <ol>
          <li><b>L1</b> checks what you think they will do.</li>
          <li><b>L2</b> checks what you think they think you will do.</li>
          <li><b>L3+</b> checks nested predictions of predictions.</li>
        </ol>
        <p className="tiny">Capture is a byproduct: structured chains, confidence, reaction time, and optional reflections feed the public commons.</p>
      </div>
    </section>
  );
}

function QueueScreen() {
  const queueSize = useAppStore((s) => s.queueSize);
  return (
    <section className="center panel queue">
      <div className="spinner" />
      <h1>Finding a human stranger…</h1>
      <p>Durable Object lobby is holding your socket at the edge. Queue position estimate: {Math.max(1, queueSize)}.</p>
      <p className="tiny">Open this app in another browser/device to test a local two-player match.</p>
    </section>
  );
}

function PlayScreen() {
  const socket = useAppStore((s) => s.socket);
  const current = useAppStore((s) => s.current)!;
  const chain = useAppStore((s) => s.chain);
  const confidence = useAppStore((s) => s.confidence);
  const setChoice = useAppStore((s) => s.setChoice);
  const setConfidence = useAppStore((s) => s.setConfidence);
  const partnerSubmitted = useAppStore((s) => s.partnerSubmitted);
  const phase = useAppStore((s) => s.phase);
  const [focusedLevel, setFocusedLevel] = useState(0);

  const submit = () => {
    if (!socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(
      JSON.stringify({
        type: "chain",
        roomId: current.roomId,
        round: current.round,
        chain,
        confidence,
        reactionMs: Math.round(performance.now() - current.startAt)
      })
    );
    useAppStore.setState({ phase: "waiting" });
  };

  if (phase === "waiting") {
    return (
      <section className="center panel queue">
        <div className="spinner small" />
        <h1>Chain submitted.</h1>
        <p>{partnerSubmitted ? "Your stranger has also submitted. Scoring the ladder…" : "Waiting for your stranger’s ladder…"}</p>
      </section>
    );
  }

  return (
    <section className="play-layout">
      <div className="panel arena">
        <div className="round-meta">
          <span>Room {current.roomId.slice(-6)}</span>
          <span>Round {current.round}</span>
          <span>You are {current.role}</span>
          <span>Max L{current.maxDepth}</span>
        </div>
        <h1>Build the belief ladder</h1>
        <LadderViz chain={chain} />
        <div className="level-tabs">
          {chain.map((_, level) => (
            <button key={level} className={focusedLevel === level ? "active" : ""} onClick={() => setFocusedLevel(level)} type="button">
              L{level}
            </button>
          ))}
        </div>
        <div className="choice-card">
          <DepthBadge level={focusedLevel} />
          <h2>{DEPTH_PROMPTS[focusedLevel]}</h2>
          <div className="symbols">
            {SYMBOLS.map((symbol) => (
              <SymbolButton key={symbol.id} symbol={symbol} selected={chain[focusedLevel] === symbol.id} onClick={() => setChoice(focusedLevel, symbol.id)} />
            ))}
          </div>
          <label className="confidence">
            <span>Confidence {confidence[focusedLevel]}%</span>
            <input type="range" min="0" max="100" value={confidence[focusedLevel]} onChange={(e) => setConfidence(focusedLevel, Number(e.target.value))} />
          </label>
          <div className="nav-row">
            <button className="ghost" disabled={focusedLevel === 0} onClick={() => setFocusedLevel((x) => Math.max(0, x - 1))} type="button">Back</button>
            <button className="ghost" disabled={focusedLevel >= current.maxDepth} onClick={() => setFocusedLevel((x) => Math.min(current.maxDepth, x + 1))} type="button">Next level</button>
            <button className="primary" onClick={submit} type="button">Submit ladder</button>
          </div>
        </div>
      </div>
      <CommonsPanel />
    </section>
  );
}

function ResultScreen() {
  const result = useAppStore((s) => s.result)!;
  const current = useAppStore((s) => s.current);
  const socket = useAppStore((s) => s.socket);
  const session = useAppStore((s) => s.session);
  const [reflection, setReflection] = useState("");
  const [reflectionResult, setReflectionResult] = useState<string>("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!current || !result) return;
    db.rounds.put({
      id: `${result.roomId}-${result.round}-${result.role}`,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      roomId: result.roomId,
      round: result.round,
      frontier: result.your.frontier,
      score: result.your.score,
      chain: result.yourChain,
      partnerChain: result.partnerChain,
      tags: ["round", `frontier-${result.your.frontier}`],
      meta: { targetDepth: result.your.targetDepth }
    });
  }, [current, result]);

  const next = () => {
    if (!socket || !current) return;
    socket.send(JSON.stringify({ type: "continue", roomId: current.roomId }));
    useAppStore.setState({ phase: "waiting" });
  };

  const reflect = async () => {
    if (!session || !reflection.trim()) return;
    setBusy(true);
    setReflectionResult("");
    try {
      const res = await submitReflection(session.token, current?.roomId, reflection);
      setReflectionResult(res.embedded ? `Embedded into D1 JSON vector space. Nearest prior reflections: ${res.nearest.length}.` : "Saved without embedding. Add the Workers AI binding to enable vector reflection search.");
    } catch (err) {
      setReflectionResult(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="play-layout">
      <div className="panel arena result">
        <div className="eyebrow">Round {result.round} · recursion frontier</div>
        <h1>You reached L{result.your.frontier}</h1>
        <p className="score-line">Score {result.your.score}% · Next training target: <b>L{result.your.targetDepth}</b></p>
        <LadderViz chain={result.yourChain} partnerChain={result.partnerChain} accuracy={result.your.accuracy} />
        <div className="accuracy-grid">
          {result.yourChain.slice(1).map((_, index) => {
            const level = index + 1;
            return (
              <div className="accuracy-card" key={level}>
                <DepthBadge level={level} ok={result.your.accuracy[level]} />
                <p>{DEPTH_PROMPTS[level]}</p>
                <strong>{result.your.accuracy[level] ? "matched" : "missed"}</strong>
              </div>
            );
          })}
        </div>
        <div className="coach-card">
          <span>Train one deeper</span>
          <p>{result.nextLesson}</p>
        </div>
        <div className="reflection-box">
          <label htmlFor="reflection">Optional reflection: what changed your model of the stranger?</label>
          <textarea id="reflection" value={reflection} onChange={(e) => setReflection(e.target.value)} placeholder="I thought they would expect me to over-correct, so…" />
          <button className="ghost" type="button" disabled={busy || reflection.trim().length < 8} onClick={reflect}>{busy ? "Embedding…" : "Add to commons"}</button>
          {reflectionResult && <p className="tiny">{reflectionResult}</p>}
        </div>
        <button className="primary" type="button" onClick={next}>Play next round</button>
      </div>
      <CommonsPanel />
    </section>
  );
}

function Shell() {
  const phase = useAppStore((s) => s.phase);
  const error = useAppStore((s) => s.error);
  const setConfig = useAppStore((s) => s.setConfig);
  const setStats = useAppStore((s) => s.setStats);
  const setError = useAppStore((s) => s.setError);

  useEffect(() => {
    getConfig().then(setConfig).catch((err) => setError(err instanceof Error ? err.message : String(err)));
    const update = () => getStats().then(setStats).catch(() => undefined);
    update();
    const id = setInterval(update, 20000);
    return () => clearInterval(id);
  }, [setConfig, setStats, setError]);

  useEffect(() => {
    if ("serviceWorker" in navigator) navigator.serviceWorker.register("/sw.js").catch(() => undefined);
  }, []);

  const title = useMemo(() => (phase === "result" ? "Review" : phase === "playing" ? "Ladder" : "Mindfold"), [phase]);

  return (
    <main>
      <header className="topbar">
        <a className="brand" href="/" aria-label="Mindfold home"><span>⌁</span> Mindfold Lab</a>
        <nav>
          <span>{title}</span>
          <a href="https://developers.cloudflare.com/" rel="noreferrer" target="_blank">Cloudflare edge</a>
          <a className="creator-link" href="https://www.linkedin.com/in/mindful-abhay/" rel="noreferrer" target="_blank">LinkedIn</a>
          <a className="creator-link coffee" href="https://buymeacoffee.com/abhaypratapsingh" rel="noreferrer" target="_blank">Buy me a coffee</a>
        </nav>
      </header>
      {error && <div className="toast" role="alert"><b>Note:</b> {error}<button onClick={() => setError(undefined)}>×</button></div>}
      {phase === "boot" || phase === "consent" ? <ConsentGate /> : null}
      {phase === "queue" || phase === "ended" ? <QueueScreen /> : null}
      {phase === "playing" || phase === "waiting" ? <PlayScreen /> : null}
      {phase === "result" ? <ResultScreen /> : null}
      <footer>
        <span>Named phenomenon: recursive mindreading / higher-order theory of mind.</span>
        <span>Anonymous device-keyed identity. Turnstile-gated human data.</span>
      </footer>
    </main>
  );
}

export default Shell;
